/**
 * SearchInput — a text field with a magnifier and an optional clear button.
 *
 * The portal had two of these. Admin's was a 1.5px border at `--radius-6` with
 * no focus ring and 0.4rem of vertical padding; the moderator's was 2px at
 * `--radius-8` with a 3px brand ring and 0.75rem — visibly a taller, heavier
 * control one click away from the other. Both are now the `Input` primitive
 * with the affordances layered on top, so a search box looks the same wherever
 * it appears and the focus ring comes from one place.
 *
 * The clear button is opt-in: pass `onClear` and it appears whenever the field
 * is non-empty.
 */

import type { InputHTMLAttributes } from 'react';
import { Input } from './form-field';
import styles from './search-input.module.css';

interface SearchInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Show a clear (×) button while the field has a value. */
  onClear?: () => void;
  /** Accessible name for the clear button. */
  clearLabel?: string;
  /** Stretch to fill a flex row. */
  grow?: boolean;
}

export function SearchInput({ onClear, clearLabel = 'Clear', grow, className, ...rest }: SearchInputProps) {
  const hasValue = Boolean(rest.value);

  return (
    <div className={[styles.wrap, grow ? styles.grow : null, className].filter(Boolean).join(' ')}>
      {/* Decorative: the placeholder already names the field, so the icon is
          hidden from assistive tech rather than announced as "image". */}
      <svg
        className={styles.icon}
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
      <Input type="text" className={styles.control} {...rest} />
      {onClear && hasValue && (
        <button type="button" className={styles.clear} onClick={onClear} aria-label={clearLabel}>
          ×
        </button>
      )}
    </div>
  );
}
